import { Alert, AlertProps } from "@jugl-web/ui-kit";
import {
  FC,
  PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { UIComponentsContext } from "./UIComponentsContext";

interface AlertQueueItem extends AlertProps {
  id: string;
  onClosed?: () => void;
}

export const UIComponentsProvider: FC<PropsWithChildren> = ({ children }) => {
  const [, setAlertQueue] = useState<AlertQueueItem[]>([]);
  const [currentAlert, setCurrentAlert] = useState<AlertQueueItem | null>(null);
  const alertIdRef = useRef(0);

  const showAlert = useCallback(
    (options: Omit<AlertProps, "isOpen" | "onRequestClose">) => {
      const id = `alert-${(alertIdRef.current += 1)}`;
      const newAlert: AlertQueueItem = {
        ...options,
        id,
      };

      setCurrentAlert((prevAlert) => {
        if (!prevAlert) {
          return newAlert;
        }
        setAlertQueue((queue) => [...queue, newAlert]);
        return prevAlert;
      });
    },
    []
  );

  const handleAlertClose = useCallback((alert: AlertQueueItem) => {
    setCurrentAlert(null);
    alert.onClosed?.();
    setTimeout(() => {
      setAlertQueue((queue) => {
        if (queue.length > 0) {
          setCurrentAlert(queue[0]);
          return queue.slice(1);
        }
        return queue;
      });
    }, 200);
  }, []);

  const contextValue = useMemo(
    () => ({
      showAlert,
    }),
    [showAlert]
  );

  return (
    <UIComponentsContext.Provider value={contextValue}>
      {children}
      {currentAlert && (
        <Alert
          {...currentAlert}
          isOpen
          onRequestClose={() => handleAlertClose(currentAlert)}
        />
      )}
    </UIComponentsContext.Provider>
  );
};
